import {
  Add01Icon,
  Delete01Icon,
  PencilEdit01Icon,
  Search01Icon,
  Tag01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';

import React, { useState } from 'react';

import { useMutation, useQuery } from 'convex/react';

import { api } from '@/../convex/_generated/api';
import type { AccountTypeId, BalanceSheetId } from '@/../convex/types';
import { AccountTypeModal } from '@/components/AccountTypeModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AuthenticatedRoute } from '@/lib/authenticatedRoute';

function AccountTypesPage() {
  const { id } = Route.useParams() as { id: string };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccountType, setEditingAccountType] =
    useState<AccountTypeId | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountTypeToDelete, setAccountTypeToDelete] =
    useState<AccountTypeId | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const accountTypes = useQuery(api.accountTypes.listForBalanceSheet, {
    balanceSheetId: id as BalanceSheetId,
  });

  const deleteAccountType = useMutation(api.accountTypes.remove);

  const handleCreate = () => {
    setEditingAccountType(null);
    setIsModalOpen(true);
  };

  const handleEdit = (accountTypeId: AccountTypeId) => {
    setEditingAccountType(accountTypeId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAccountType(null);
  };

  const handleDeleteClick = (accountTypeId: AccountTypeId) => {
    setAccountTypeToDelete(accountTypeId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!accountTypeToDelete) return;

    try {
      await deleteAccountType({ id: accountTypeToDelete });
      setDeleteDialogOpen(false);
      setAccountTypeToDelete(null);
    } catch (error) {
      console.error('Failed to delete account type:', error);
      alert('Cannot delete account type that is being used by accounts');
    }
  };

  const filteredAccountTypes =
    accountTypes?.filter(accountType => {
      const query = searchQuery.toLowerCase();
      return (
        accountType.label.toLowerCase().includes(query) ||
        accountType.side.toLowerCase().includes(query) ||
        accountType.defaultLiquidity.toLowerCase().includes(query)
      );
    }) || [];

  const totalPages = Math.ceil(filteredAccountTypes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAccountTypes = filteredAccountTypes.slice(
    startIndex,
    endIndex
  );

  const currentPageNumber = currentPage;

  if (!accountTypes) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Account Types</h1>
          <p className="text-muted-foreground">
            Manage the types of accounts available for this balance sheet
          </p>
        </div>
      </div>

      {accountTypes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <HugeiconsIcon
              icon={Tag01Icon}
              size={48}
              strokeWidth={2}
              className="text-muted-foreground mb-4"
            />
            <h3 className="text-lg font-semibold mb-2">No account types yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first account type to get started with organizing your
              accounts.
            </p>
            <Button onClick={handleCreate}>
              <HugeiconsIcon
                icon={Add01Icon}
                size={16}
                strokeWidth={2}
                className="mr-2"
              />
              Add Account Type
            </Button>
          </CardContent>
        </Card>
      ) : filteredAccountTypes.length === 0 ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <CardTitle className="flex-shrink-0">
                {accountTypes.length} Account Type
                {accountTypes.length !== 1 ? 's' : ''}
              </CardTitle>
              <div className="relative flex-1">
                <HugeiconsIcon
                  icon={Search01Icon}
                  size={16}
                  strokeWidth={2}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  placeholder="Search account types..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchQuery(e.target.value)
                  }
                  className="pl-9"
                />
              </div>
              <Button onClick={handleCreate}>
                <HugeiconsIcon
                  icon={Add01Icon}
                  size={16}
                  strokeWidth={2}
                  className="mr-2"
                />
                Add Account Type
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <HugeiconsIcon
              icon={Tag01Icon}
              size={48}
              strokeWidth={2}
              className="text-muted-foreground mb-4"
            />
            <h3 className="text-lg font-semibold mb-2">
              No matching account types
            </h3>
            <p className="text-muted-foreground text-center">
              No account types match your search: &quot;{searchQuery}&quot;
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <CardTitle className="flex-shrink-0">
                {filteredAccountTypes.length} Account Type
                {filteredAccountTypes.length !== 1 ? 's' : ''}
                {filteredAccountTypes.length > itemsPerPage && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    (showing {startIndex + 1}-
                    {Math.min(endIndex, filteredAccountTypes.length)})
                  </span>
                )}
              </CardTitle>
              <div className="relative flex-1">
                <HugeiconsIcon
                  icon={Search01Icon}
                  size={16}
                  strokeWidth={2}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  placeholder="Search account types..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchQuery(e.target.value)
                  }
                  className="pl-9"
                />
              </div>
              <Button onClick={handleCreate}>
                <HugeiconsIcon
                  icon={Add01Icon}
                  size={16}
                  strokeWidth={2}
                  className="mr-2"
                />
                Add Account Type
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <TooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Side</TableHead>
                    <TableHead>Default Liquidity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Accounts</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedAccountTypes.map(accountType => (
                    <TableRow key={accountType._id}>
                      <TableCell className="font-medium">
                        {accountType.label}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={
                            accountType.side === 'Asset'
                              ? 'bg-green-100 text-green-900 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/30'
                              : 'bg-red-100 text-red-900 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/30'
                          }
                        >
                          {accountType.side}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={
                            accountType.defaultLiquidity === 'Liquid'
                              ? 'bg-blue-100 text-blue-900 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/30'
                              : 'bg-amber-100 text-amber-900 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/30'
                          }
                        >
                          {accountType.defaultLiquidity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            accountType.isActive ? 'default' : 'secondary'
                          }
                        >
                          {accountType.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {accountType.accountCount}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(accountType._id)}
                          >
                            <HugeiconsIcon
                              icon={PencilEdit01Icon}
                              size={16}
                              strokeWidth={2}
                            />
                          </Button>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteClick(accountType._id)
                                  }
                                  disabled={accountType.accountCount > 0}
                                >
                                  <HugeiconsIcon
                                    icon={Delete01Icon}
                                    size={16}
                                    strokeWidth={2}
                                  />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {accountType.accountCount > 0 && (
                              <TooltipContent>
                                <p>
                                  Cannot delete: {accountType.accountCount}{' '}
                                  account
                                  {accountType.accountCount !== 1
                                    ? 's'
                                    : ''}{' '}
                                  using this type
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TooltipProvider>

            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className={
                          currentPageNumber === 1
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }
                      />
                    </PaginationItem>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPageNumber <= 3) {
                        pageNum = i + 1;
                      } else if (currentPageNumber >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPageNumber - 2 + i;
                      }

                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            onClick={() => setCurrentPage(pageNum)}
                            isActive={currentPageNumber === pageNum}
                            className="cursor-pointer"
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() =>
                          setCurrentPage(p => Math.min(totalPages, p + 1))
                        }
                        className={
                          currentPageNumber === totalPages
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <AccountTypeModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        balanceSheetId={id as BalanceSheetId}
        accountTypeId={editingAccountType}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this
              account type from your balance sheet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export const Route = createFileRoute('/balance-sheet/$id/account-types')({
  component: AuthenticatedRoute(AccountTypesPage),
});
